import type { ISODateTimeString, UUID } from './shared';

/**
 * Amplitude surface labels for pin/unpin events. Emitted as `source_context` on
 * every pin lifecycle event (clicked/confirmed/succeeded/failed and
 * pin_limit_reached). Keep narrow so typos can't ship and Amplitude funnels
 * stay clean.
 *
 * Never named `source` — that's the base attribution field useAmplitudeTracking
 * adds (`"web"` / `"native"`).
 */
export type PinSourceContext =
  // QuestionCard menu-triggered actions (wired in handleConfirmPin/Unpin).
  | 'community_feed_card_menu'
  // Render-surface fallbacks forwarded from the sourceContext prop when the
  // menu handlers don't override.
  | 'feed'
  | 'profile'
  | 'unknown';

export interface PinResponse {
  space_id: UUID;
  question_id: UUID;
  pinned_at: ISODateTimeString;
  pinned_by_user_id: UUID | null;
  pinned_by_name: string | null;
}

export type PinErrorCode = (typeof PinErrorCode)[keyof typeof PinErrorCode];

export const PinErrorCode = {
  /** Question is not linked to this space (404). */
  question_not_in_space: 'QUESTION_NOT_IN_SPACE',
  /** Space already has MAX_PINS_PER_SPACE pinned questions (409). */
  pin_limit_reached: 'PIN_LIMIT_REACHED',
} as const;

export interface PinLimitReachedDetail {
  code: typeof PinErrorCode.pin_limit_reached;
  limit: number;
  current: number;
}

export interface QuestionNotInSpaceDetail {
  code: typeof PinErrorCode.question_not_in_space;
}

export type PinErrorDetail = PinLimitReachedDetail | QuestionNotInSpaceDetail;
